import {useEffect, type RefObject} from 'react';

const HALF = 2;
const HALF_CIRCLE_RAD = Math.PI / HALF;


export const easeOutSine = (progress: number): number => Math.sin(progress * HALF_CIRCLE_RAD);

export const roundReelValue = (value: number): number => Math.round(value);

export const reelStepPx = (cardWidth: number, gap: number): number => cardWidth + gap;

export const reelSetWidthPx = (itemCount: number, cardWidth: number, gap: number): number => itemCount * reelStepPx(cardWidth, gap);

export const reelViewportCenterPx = (viewportWidth: number): number => Math.round(viewportWidth / HALF);


export const repeatItemsForReel = <Item>(items: Item[], repeatCount: number): Item[] => Array.from(
    {length: repeatCount},
    () => items
).flat();

export const reelCopiesForEndIndex = (itemCount: number, endIndex: number, rightPadding: number): number => {
    const needed = endIndex + rightPadding + 1;

    return Math.max(HALF, Math.ceil(needed / Math.max(itemCount, 1)));
};

export const reelEndIndex = (startIndex: number, itemCount: number, rotations: number): number => startIndex + rotations * Math.max(itemCount, 1);

interface ReelTargetEnd {
    itemCount: number
    rotations: number
    startIndex: number
    targetIndex: number
}

export const reelTargetEndIndex = ({itemCount, rotations, startIndex, targetIndex}: ReelTargetEnd): number => {
    const count = Math.max(itemCount, 1);
    const startInSet = (startIndex % count + count) % count;
    const targetInSet = (targetIndex % count + count) % count;
    const shift = (targetInSet - startInSet + count) % count;

    return startIndex + rotations * count + shift;
};

export const reelTranslateX = (index: number, step: number): string => `translateX(${-index * step}px)`;

export interface ReelLayout {
    cardWidth: number
    gap: number
    viewportWidth: number
}

export const reelScrollLeftToCenterIndex = (index: number, layout: ReelLayout): number => {
    const cardCenter = index * reelStepPx(layout.cardWidth, layout.gap) + layout.cardWidth / HALF;

    return roundReelValue(cardCenter - reelViewportCenterPx(layout.viewportWidth));
};

export const reelCenteredIndexFromScrollLeft = (
    scrollLeft: number,
    layout: ReelLayout,
    repeatedCount: number
): number => {
    if (repeatedCount === 0) {
        return 0;
    }

    const raw = (roundReelValue(scrollLeft) + reelViewportCenterPx(layout.viewportWidth) - layout.cardWidth / HALF) /
        reelStepPx(layout.cardWidth, layout.gap);

    return Math.max(0, Math.min(repeatedCount - 1, Math.round(raw)));
};

export const reelValueInPeriod = (raw: number, periodWidth: number): number => {
    const mod = raw % periodWidth;

    return mod < 0 ? mod + periodWidth : mod;
};


export const computeSpinEndScrollLeft = (
    startLeft: number,
    periodWidth: number,
    targetScrollForSecondRepeat: number
): number => {
    const baseEndLeft = targetScrollForSecondRepeat + periodWidth;
    const minEndLeft = startLeft + periodWidth;
    const extraPeriods = Math.max(0, Math.ceil((minEndLeft - baseEndLeft) / periodWidth));

    return baseEndLeft + extraPeriods * periodWidth;
};

interface AnimateReelSpinOptions {
    apply: (value: number) => void
    durationMs: number
    easing?: (progress: number) => number
    from: number
    onDone?: () => void
    to: number
}


export const animateReelSpin = (options: AnimateReelSpinOptions): (() => void) => {
    const {apply, durationMs, from, onDone, to} = options;
    const easing = options.easing || easeOutSine;
    let rafId: number | null = null;
    let cancelled = false;
    const startTime = performance.now();

    const tick = (now: number): void => {
        if (cancelled) {
            return;
        }

        const progress = Math.min((now - startTime) / durationMs, 1);

        apply(roundReelValue(from + (to - from) * easing(progress)));

        if (progress < 1) {
            rafId = requestAnimationFrame(tick);
            return;
        }

        apply(roundReelValue(to));
        onDone?.();
    };

    rafId = requestAnimationFrame(tick);

    return (): void => {
        cancelled = true;

        if (rafId !== null) {
            cancelAnimationFrame(rafId);
        }
    };
};

interface UseReelSpinOptions {
    delayMs: number
    durationMs: number
    enabled: boolean
    endIndex: number
    startIndex: number
    step: number
}


export const useReelSpin = (trackRef: RefObject<HTMLDivElement | null>, options: UseReelSpinOptions): void => {
    const {delayMs, durationMs, enabled, endIndex, startIndex, step} = options;

    useEffect(() => {
        const track = trackRef.current;
        let cancelSpin: (() => void) | null = null;
        let spinTimer: number | null = null;

        if (track) {
            const apply = (value: number): void => {
                track.style.transform = `translateX(${value}px)`;
            };

            apply(-startIndex * step);

            if (enabled) {
                spinTimer = window.setTimeout(() => {
                    cancelSpin = animateReelSpin({
                        apply,
                        durationMs,
                        from: -startIndex * step,
                        to: -endIndex * step
                    });
                }, delayMs);
            }
        }

        return () => {
            if (spinTimer !== null) {
                window.clearTimeout(spinTimer);
            }

            cancelSpin?.();
        };
    }, [
        delayMs,
        durationMs,
        enabled,
        endIndex,
        startIndex,
        step,
        trackRef
    ]);
};
